import type { BlogPost } from "../content/blog";
import { sortBlogPosts } from "../content/blog";
import { productionUrl, siteName } from "./site";

/** Canonical origin for search engines — never a Netlify preview or localhost URL. */
export const productionOrigin = productionUrl.replace(/\/$/, "");

/** Public section for the climate reviews. Image files stay under `/blog/`. */
export const reviewsPath = "/reviews";

export function reviewArticlePath(slug: string): string {
  return `${reviewsPath}/${slug}`;
}

export const defaultTitle = `${siteName} · Arboretum Labs`;

export const defaultDescription =
  "whatIsUp — regime intelligence for atmospheric blocking and persistent extreme-weather risk. Arboretum Labs.";

/** Existing site mark. A dedicated 1200×630 share image can replace this path later. */
export const defaultSocialImagePath = "/favicon.png";

export function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }
  const path = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
  return `${productionOrigin}${path}`;
}

export const defaultSocialImageUrl = absoluteUrl(defaultSocialImagePath);

export type PublicPageSeo = {
  title: string;
  description: string;
};

/**
 * Public, indexable routes other than individual articles.
 * Article URLs come from `content/blog` and are added in `sitemapEntries`.
 * Redirects, 404s, and internal-only paths are intentionally omitted.
 */
export const publicPageSeo: Record<string, PublicPageSeo> = {
  "/": {
    title: defaultTitle,
    description: defaultDescription,
  },
  "/explore": {
    title: `Explore atmospheric blocking — ${siteName}`,
    description:
      "Explore maps and timelines of European atmospheric blocking events, including CNN detection scores, Z500 fields, and Grad-CAM explanations.",
  },
  "/science": {
    title: `Atmospheric blocking science — ${siteName}`,
    description:
      "How atmospheric blocking drives persistent extremes, why standard indices disagree, and the science behind whatIsUp's blocking detection.",
  },
  [reviewsPath]: {
    title: `Climate reviews — ${siteName}`,
    description:
      "Climate reviews on extreme weather, from a hot European summer to El Niño and the forecasts around them.",
  },
};

export const publicSitemapPaths = Object.keys(publicPageSeo);

export function normalizePathname(pathname: string): string {
  const withoutQuery = (pathname.split(/[?#]/)[0] ?? "").trim();
  if (!withoutQuery || withoutQuery === "/") {
    return "/";
  }
  const trimmed = withoutQuery.replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
}

export function canonicalUrlForPath(pathname: string): string {
  const path = normalizePathname(pathname);
  return path === "/" ? `${productionOrigin}/` : `${productionOrigin}${path}`;
}

export type OgType = "website" | "article";

export type ResolvedPageSeo = {
  title: string;
  description: string;
  canonicalUrl: string | null;
  robots: "index, follow" | "noindex, nofollow";
  ogType: OgType;
  image: string | null;
  jsonLd: Record<string, unknown> | null;
};

export function websiteJsonLd(page: {
  title: string;
  description: string;
  canonicalUrl: string;
}): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: page.title,
    description: page.description,
    url: page.canonicalUrl,
    isPartOf: {
      "@type": "WebSite",
      name: siteName,
      url: `${productionOrigin}/`,
    },
  };
}

export function blogPostingJsonLd(post: BlogPost): Record<string, unknown> {
  const pageUrl = canonicalUrlForPath(reviewArticlePath(post.slug));
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.description,
    image: absoluteUrl(post.heroImage),
    datePublished: post.datePublished,
    dateModified: post.dateModified,
    author: {
      "@type": "Person",
      name: post.author,
    },
    publisher: {
      "@type": "Organization",
      name: siteName,
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": pageUrl,
    },
  };
}

export function publishedReviews(posts: BlogPost[]): BlogPost[] {
  return sortBlogPosts(posts).filter((post) => post.status !== "coming-soon");
}

export function blogIndexJsonLd(posts: BlogPost[]): Record<string, unknown> {
  const page = publicPageSeo[reviewsPath];
  return {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: page.title,
    description: page.description,
    url: canonicalUrlForPath(reviewsPath),
    blogPost: publishedReviews(posts).map((post) => ({
      "@type": "BlogPosting",
      headline: post.title,
      datePublished: post.datePublished,
      url: canonicalUrlForPath(reviewArticlePath(post.slug)),
    })),
  };
}

function indexablePage(
  page: PublicPageSeo,
  path: string,
  jsonLd: Record<string, unknown>,
): ResolvedPageSeo {
  return {
    title: page.title,
    description: page.description,
    canonicalUrl: canonicalUrlForPath(path),
    robots: "index, follow",
    ogType: "website",
    image: defaultSocialImageUrl,
    jsonLd,
  };
}

export function notFoundSeo(): ResolvedPageSeo {
  return {
    title: `Page not found — ${siteName}`,
    description: "This address is not a page on whatIsUp.",
    canonicalUrl: null,
    robots: "noindex, nofollow",
    ogType: "website",
    image: null,
    jsonLd: null,
  };
}

export function seoForPath(pathname: string, posts: BlogPost[] = []): ResolvedPageSeo {
  const path = normalizePathname(pathname);
  const page = publicPageSeo[path];
  if (page) {
    const canonicalUrl = canonicalUrlForPath(path);
    const jsonLd =
      path === reviewsPath
        ? blogIndexJsonLd(posts)
        : websiteJsonLd({
            title: page.title,
            description: page.description,
            canonicalUrl,
          });
    return indexablePage(page, path, jsonLd);
  }

  const articleMatch = path.match(/^\/reviews\/([^/]+)$/);
  if (articleMatch) {
    const post = posts.find((item) => item.slug === articleMatch[1]);
    if (!post) {
      return notFoundSeo();
    }
    if (post.status === "coming-soon") {
      return {
        title: `${post.title} — ${siteName}`,
        description: post.description,
        canonicalUrl: canonicalUrlForPath(path),
        robots: "noindex, nofollow",
        ogType: "article",
        image: absoluteUrl(post.heroImage),
        jsonLd: null,
      };
    }
    return {
      title: `${post.title} — ${siteName}`,
      description: post.description,
      canonicalUrl: canonicalUrlForPath(path),
      robots: "index, follow",
      ogType: "article",
      image: absoluteUrl(post.heroImage),
      jsonLd: blogPostingJsonLd(post),
    };
  }

  return notFoundSeo();
}

export type SitemapEntry = {
  loc: string;
  lastmod?: string;
};

export function sitemapEntries(posts: BlogPost[]): SitemapEntry[] {
  const pages = publicSitemapPaths.map((path) => ({ loc: canonicalUrlForPath(path) }));
  const articles = publishedReviews(posts).map((post) => ({
    loc: canonicalUrlForPath(reviewArticlePath(post.slug)),
    lastmod: post.dateModified,
  }));
  return [...pages, ...articles];
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function renderSitemapXml(entries: SitemapEntry[]): string {
  const urls = entries
    .map((entry) => {
      const lastmod = entry.lastmod
        ? `\n    <lastmod>${escapeXml(entry.lastmod)}</lastmod>`
        : "";
      return `  <url>\n    <loc>${escapeXml(entry.loc)}</loc>${lastmod}\n  </url>`;
    })
    .join("\n");
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls}\n</urlset>\n`;
}

export function indexablePaths(posts: BlogPost[]): string[] {
  return [
    ...publicSitemapPaths,
    ...sortBlogPosts(posts).map((post) => reviewArticlePath(post.slug)),
  ];
}
